import { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { PreviewBanner } from "@/components/sci/PreviewBanner";
import { useSciPreview, fmtDate } from "@/components/sci/useSciPreview";
import { SciNotFound as NotFound, SciLoadError } from "@/components/sci/SciNotFound";
import { renderBlocks, renderNode } from "@/components/sci/student/Blocks";
import { SectionPager } from "@/components/sci/student/SectionPager";
import { WhatNext } from "@/components/sci/student/WhatNext";
import { useGuideProgress } from "@/components/sci/student/useGuideProgress";
import { GuideAdmin } from "@/components/sci/student/GuideAdmin";
import { OtherGuides } from "@/components/sci/student/OtherGuides";
import { useCmsRoles } from "@/hooks/useCmsRoles";
import { useUkRanking } from "@/lib/subjectGuides";
import { FastFitStrip, ExploreFitLink } from "@/components/sci/student/FastFitStrip";
import { useFastFitGuide } from "@/components/sci/student/useFastFitGuide";
import s from "@/components/sci/student/guide.module.css";
import { subjectArt } from "@/lib/subjectArt";
import { SubjectsLineNote } from "@/components/sci/student/SubjectsLineNote";

// index.html's site-wide description has no Helmet marker, so it would sit beside the guide's own; park it while mounted.
function useOwnDescription() {
  useEffect(() => {
    const el = document.querySelector('meta[name="description"]:not([data-rh])');
    const parent = el?.parentNode;
    el?.remove();
    return () => { if (el && parent) parent.prepend(el); };
  }, []);
}

function GuideHead({ seo }) {
  useOwnDescription();
  return (
    <Helmet>
      <title>{`${seo.title} | ${seo.site_name}`}</title>
      <meta name="description" content={seo.description} />
      <meta name="robots" content={seo.robots} />
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      {seo.canonical && <meta property="og:url" content={seo.canonical} />}
      <script type="application/ld+json">{JSON.stringify(seo.jsonld)}</script>
    </Helmet>
  );
}

export default function SciStudentGuide({ publicMode = false }) {
  const { slug } = useParams();
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const { loading, data: d, missing, error, retry, refresh } = useSciPreview(publicMode ? `/subjects/${slug}` : `/sci/preview/subjects/${slug}/student`);
  const { isAdmin } = useCmsRoles();
  const ukRanking = useUkRanking(publicMode ? d?.subject.name : null);
  const moved = useRef(false);
  const ids = d ? d.nav.map((t) => t.id) : [];
  const page = ids.includes(hash.slice(1)) ? hash.slice(1) : "quick";
  const { done, finish } = useGuideProgress(slug);
  const ff = useFastFitGuide(publicMode ? search : "", slug);
  // Inside a Fast Fit context, generic guide-to-guide links keep the active family so they can't silently drop it.
  const ffSuffix = ff.status === "ready" && ff.params?.family ? `?from=fast-fit&family=${encodeURIComponent(ff.params.family)}` : "";
  useEffect(() => {
    const el = d && document.querySelector(`[data-testid="student-nav-${page}"]`);
    const list = el?.closest("ol");
    if (list) list.scrollTo({ left: el.parentElement.offsetLeft - 16, behavior: "smooth" });
  }, [page, d]);
  // App's ScrollToTop scrolls to the #anchor (honouring reduced motion); here we only move keyboard focus.
  useEffect(() => {
    if (!d || (!moved.current && !hash)) return;
    document.getElementById(`student-section-${page}`)?.focus({ preventScroll: true });
  }, [page, d]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="min-h-[60vh]" data-testid="student-guide-loading" />;
  if (missing) return <NotFound />;
  if (error) return <SciLoadError onRetry={retry} />;
  // Moving on to the very next section (pager card or in-content button) marks the current one finished.
  const go = (p, forward = false) => {
    if (forward && ids.indexOf(p) === ids.indexOf(page) + 1) finish(page);
    moved.current = true;
    if (p !== page) navigate({ hash: `#${p}` });
  };
  const seo = d.seo;
  const art = subjectArt(d.subject.name, slug);
  const ctx = { slug, w: d.widgets, ai: d.ai_outlook, go, kicker: seo.h1_kicker };
  const href = (r) => (r.guide ? (publicMode ? `/subjects/${r.guide}` : `/preview/sci/subjects/${r.guide}/student`) : r.href);
  const ranking = publicMode ? ukRanking : seo.related.find((r) => r.href?.startsWith("/rankings/"))?.href;
  return (
    <div className={`${s.guide} min-h-screen`} data-testid="student-guide-page">
      <GuideHead seo={seo} />
      {!publicMode && (
        <PreviewBanner runId={d._schema} label="Private preview · admins only · editorial review"
          note="Not visible to students. Approved editorial draft; cited sources have not been through WUA data-layer source approval." />
      )}
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {publicMode && <SubjectsLineNote />}
        {publicMode && (ff.status === "off" ? <ExploreFitLink /> : <FastFitStrip ff={ff} subjectId={d.subject.subject_id} name={d.subject.name} />)}
        <article className={s.frame} aria-label={seo.title}>
          <header className={s.head}>
            <span className={s.brand}>{publicMode ? <Link to={`/subjects${ffSuffix}`} data-testid="student-guide-directory-link">Which University Advisor / Subjects</Link> : "Which University Advisor / Subjects"}</span>
            <span className={s.meta} data-testid="student-guide-status">{d.subject.name} · {publicMode ? `Subject guide · version ${d.version}` : "Private preview · editorial review"}</span>
            <span className={s.meta} data-testid="student-guide-owner">{publicMode ? `${seo.owner} · Updated ${fmtDate(seo.reviewed)} · Sources checked ${fmtDate(seo.sources_checked)}` : `${seo.owner} · Reviewed ${fmtDate(seo.reviewed)} · Sources below`}</span>
          </header>
          {art && (
            <figure className="my-6 overflow-hidden rounded-xl bg-white" data-testid="student-guide-hero-art">
              <img src={art.src} alt={art.alt} className="block h-auto w-full" width="1672" height="941" />
            </figure>
          )}
          <nav className={s.nav} aria-label={`${d.subject.name} guide sections`} data-testid="student-stepper" data-finished={done.size}>
            <ol className={s.stepList}>
              {d.nav.map((t, n) => {
                const cls = page === t.id ? s.navStepActive : done.has(t.id) ? s.navStepVisited : "";
                return (
                  <li key={t.id} className={s.stepItem}>
                    <button type="button" className={`${s.navStep} ${cls}`} aria-pressed={String(page === t.id)}
                      aria-current={page === t.id ? "step" : undefined} aria-controls={`student-section-${t.id}`}
                      onClick={() => go(t.id)} data-testid={`student-nav-${t.id}`}>
                      <span className={s.navStepBadge} aria-hidden="true">{done.has(t.id) && page !== t.id ? "✓" : n + 1}</span>
                      <span><span className="sr-only">Step {n + 1}: </span>{t.label}{done.has(t.id) && <span className="sr-only"> (finished)</span>}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>
          <main className={s.main}>
            {Object.entries(d.sections).map(([id, sec]) => [
              <span key={`a-${id}`} id={id} className={s.anchor} aria-hidden="true" />,
              <section key={id} id={`student-section-${id}`} aria-label={sec.aria} hidden={page !== id} tabIndex={-1}
                className={s.section} data-testid={`student-section-${id}`}>
                {renderBlocks(sec.blocks, ctx)}
              </section>,
            ])}
            <SectionPager nav={d.nav} page={page} go={go} sections={d.sections} onEnd={() => finish(page)}
              endText={publicMode || seo.related.length ? "Sources, then links to other subject guides, are below." : "Sources are below."} />
            {page === ids[ids.length - 1] && (
              <WhatNext nav={d.nav} done={done} go={go} name={d.subject.name} ranking={ranking} publicMode={publicMode} />
            )}
            {renderNode(d.sources, ctx, "sources")}
{publicMode ? <OtherGuides seo={seo} suffix={ffSuffix} /> : seo.related.length > 0 && (
            <nav className={s.related} aria-label="Related" data-testid="student-related">
              <h3>Related</h3>
              <ul>{seo.related.map((r) => <li key={r.label}><Link to={href(r)} data-testid={`student-related-${r.guide || "ranking"}`}>{r.label}</Link></li>)}</ul>
            </nav>
            )}
          </main>
          <footer className={s.foot} data-testid="student-guide-footer">
            {publicMode ? "Examples illustrate the subject · Reactions, notes and estimates stay on this page and are not saved · Signed-in students' section ticks are remembered"
              : "Private preview for editorial review · Examples illustrate the subject · Reactions, notes and estimates stay on this page and are not saved"}
            <br /><span className={s.status}>{d.provenance.basis}. {d.provenance.source_approval}.</span>
          </footer>
        </article>
        {publicMode && isAdmin && <GuideAdmin slug={slug} onPublished={refresh} />}
      </div>
    </div>
  );
}
